import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

function SectionCard({ title, subtitle, children }) {
  return (
    <Card>
      <CardContent>
        <Stack spacing={1}>
          <Box>
            <Typography variant="h6" fontWeight={900} sx={{ color: "#fff" }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />
        {children}
      </CardContent>
    </Card>
  );
}

const searchFieldSx = {
  "& .MuiInputLabel-root": {
    color: "#fff",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#fff",
  },
  "& .MuiInputBase-input": {
    color: "#fff",
    WebkitTextFillColor: "#fff",
  },
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    "& fieldset": {
      borderColor: "rgba(255,255,255,0.3)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255,255,255,0.55)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#fff",
    },
  },
};

export default function ProfileFaqPage() {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState("");

  const filteredSections = useMemo(() => {
    const sections = t("faq.sections", { returnObjects: true });
    const q = query.trim().toLowerCase();

    if (!q) return sections;

    return sections
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item) =>
            item.q.toLowerCase().includes(q) ||
            item.a.toLowerCase().includes(q)
        ),
      }))
      .filter((section) => section.items.length > 0);
  }, [query, i18n.language]);

  const totalQuestions = filteredSections.reduce(
    (sum, section) => sum + section.items.length,
    0
  );

  return (
    <DashboardLayout>
      <Box py={3}>
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight={900} sx={{ color: "#fff" }}>
            {t("faq.pageTitle")}
          </Typography>
          <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.82)" }}>
            {t("faq.pageSubtitle")}
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
            {t("faq.subscriptionActive")}
          </Typography>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SectionCard
              title={t("faq.searchSectionTitle")}
              subtitle={t("faq.searchSectionSubtitle")}
            >
              <Stack spacing={2}>
                <TextField
                  label={t("faq.searchLabel")}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  fullWidth
                  sx={searchFieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon sx={{ color: "#fff" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    label={t("faq.resultsChip", { count: totalQuestions })}
                    sx={{
                      color: "#fff",
                      bgcolor: "rgba(255,255,255,0.12)",
                      border: "1px solid rgba(255,255,255,0.22)",
                    }}
                  />
                  <Chip
                    label={t("faq.sectionsChip", { count: filteredSections.length })}
                    sx={{
                      color: "#fff",
                      bgcolor: "rgba(255,255,255,0.12)",
                      border: "1px solid rgba(255,255,255,0.22)",
                    }}
                  />
                </Stack>
              </Stack>
            </SectionCard>
          </Grid>

          {filteredSections.length === 0 ? (
            <Grid item xs={12}>
              <SectionCard
                title={t("faq.noResultsTitle")}
                subtitle={t("faq.noResultsSubtitle")}
              >
                <Typography sx={{ color: "#fff" }}>
                  {t("faq.noMatchText")}
                </Typography>
              </SectionCard>
            </Grid>
          ) : (
            filteredSections.map((section) => (
              <Grid item xs={12} key={section.title}>
                <SectionCard
                  title={section.title}
                  subtitle={t("faq.questionsCount", { count: section.items.length })}
                >
                  <Stack spacing={1.5}>
                    {section.items.map((item, idx) => (
                      <Accordion
                        key={`${section.title}-${idx}`}
                        disableGutters
                        sx={{
                          bgcolor: "rgba(255,255,255,0.04)",
                          color: "#fff",
                          border: "1px solid rgba(255,255,255,0.12)",
                          borderRadius: "12px !important",
                          overflow: "hidden",
                          boxShadow: "none",
                          "&:before": {
                            display: "none",
                          },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                          sx={{
                            "& .MuiAccordionSummary-content": {
                              my: 1.5,
                            },
                          }}
                        >
                          <Typography fontWeight={800} sx={{ color: "#fff" }}>
                            {item.q}
                          </Typography>
                        </AccordionSummary>

                        <AccordionDetails
                          sx={{
                            borderTop: "1px solid rgba(255,255,255,0.08)",
                            bgcolor: "rgba(255,255,255,0.02)",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(255,255,255,0.88)",
                              lineHeight: 1.8,
                            }}
                          >
                            {item.a}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Stack>
                </SectionCard>
              </Grid>
            ))
          )}
        </Grid>
      </Box>
    </DashboardLayout>
  );
}